import { ResolvedOOIWidgetStatics } from '@wix/editor-platform-ooi-metadata-resolver';

import { CssPropertyItem, InlineElement } from '@wix/component-protocol/schema';
import { EditorElementBuilder } from '@wix/component-protocol/builder';
import { OOIManifestBuilder } from '@wix/editor-platform-builder-manifest-adapter';

type ElementsPanelData = ResolvedOOIWidgetStatics['elementsPanel'];
type InnerElementRecord = Record<string, InlineElement>;

export const buildManifestFromOOIMetadata = (
  metadata: ResolvedOOIWidgetStatics,
  isMobile?: boolean,
) => {
  const { innerElements, ...rest } = metadata;

  const ooiManifest = new OOIManifestBuilder({
    statics: rest,
    nativeSettingsPanelUrl: null,
    layout: null,
  }).build({
    isMobile: isMobile ?? false,
  });

  const elementsPanel = metadata.elementsPanel;

  if (!elementsPanel?.length) {
    return {};
  }

  const resolveChildrenBranch = (
    children: ElementsPanelData,
  ): InnerElementRecord =>
    children?.length
      ? children.reduce((acc, child) => {
          const elementBuilder = new EditorElementBuilder();
          const children = child.children as ElementsPanelData;

          const childrenElements = children?.length
            ? resolveChildrenBranch(children)
            : {};

          const key = (('key' in child ? child.key : undefined) ??
            child.displayName) as string;

          const isGrouping = 'type' in child && child.type === 'grouping';
          const hasKey = 'key' in child && child.key;

          elementBuilder.setDisplayName(child.displayName);

          if (!isGrouping && hasKey) {
            elementBuilder.setCssProperties({
              display: {
                display: {
                  displayValues: ['block', 'none'] as NonNullable<
                    CssPropertyItem['display']
                  >['displayValues'],
                },
                defaultValue:
                  ooiManifest.editorElement.cssCustomProperties?.[key]
                    ?.defaultValue ?? 'none',
              },
            });
          }

          const childElementsKeys = Object.keys(childrenElements);

          childElementsKeys.forEach((childKey) => {
            elementBuilder.setInlineElement(
              childKey,
              childrenElements[childKey],
            );
          });

          acc[key.replace(/\s+/g, '_')] =
            elementBuilder.build() as InlineElement;

          return acc;
        }, {} as InnerElementRecord)
      : {};

  const elements = resolveChildrenBranch(metadata.elementsPanel);

  const manifest = new EditorElementBuilder().setDisplayName('root');

  if (ooiManifest.editorElement.displayFilters) {
    manifest.setDisplayFilters(ooiManifest.editorElement.displayFilters);
  }

  if (ooiManifest.editorElement.presets) {
    manifest.setPresets(ooiManifest.editorElement.presets);
  }
  if (ooiManifest.editorElement.states) {
    manifest.setStates(ooiManifest.editorElement.states);
  }

  Object.entries(elements).forEach(([key, value]) => {
    manifest.setInlineElement(key, value);
  });

  return manifest.build();
};
